import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { SearchInput } from "components/common/SearchInput";
import { useAutoScrollToRef } from "hooks/useAutoScrollToRef";
import { GroupsApprovalBarV3 } from "../GroupsApprovalBarV3";
import { ElementGroupScrollableDivideList } from "../../ReviewPage/ElementGroupsPanel/ElementGroupScrollableDivideList";
import { ElementGroupDefaultRow } from "../../ReviewPage/ElementGroupsPanel/ElementGroupDefaultRow";
import { ApprovalStatusCheckbox } from "../../ReviewPage/ApprovalStatusCheckbox";
import { BoardPageInspectorV3 } from "../BoardPageInspectorV3";
import { TabSortControl } from "../TabSortControl";
import { sortTabList } from "../utils/sortTabList";
import type { BoardItemTypeV3 } from "../utils/types";
import type { QuantityGroupsTabV3Props } from "./types";

export const QuantityGroupsTabV3 = ({
  canEdit,
  boardSearch,
  onBoardSearchChange,
  sort,
  onSortChange,
  selectedDiagram,
  selectedItemType,
  selectedOccurrenceIndex,
  selectedDiagramItemTypeIds,
  selectedDiagramRecordIndex,
  allDiagramItemsApproved,
  isLoadingApprovals,
  isQuantityGroupApproved,
  onOpenBulkApproval,
  onToggleQuantityGroupApproval,
  onSelectItemType,
  onSelectOccurrence,
  isTraversing,
  onStartTraversing,
  onStopTraversing,
  seenIds,
  onGoNextUnseen,
  onPrevOccurrence,
  onNextOccurrence,
  boqPositions,
  onSaveLvMatch,
  onDeleteItemGroup,
}: QuantityGroupsTabV3Props) => {
  const { t } = useTranslation();

  const { containerRef, targetRef } = useAutoScrollToRef<
    HTMLDivElement,
    HTMLLIElement
  >({
    resetDep: `${selectedItemType?.diagram_item_type_id ?? "none"}-${selectedDiagramRecordIndex}`,
    options: { block: "start" },
  });

  const handleItemTypeClick = useCallback(
    (itemType: BoardItemTypeV3) => {
      if (
        itemType.diagram_item_type_id !== selectedItemType?.diagram_item_type_id
      ) {
        trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_GROUP_SELECTED, {
          groupKind: "quantities",
          groupTypeId: String(itemType.diagram_item_type_id),
          elementCount: itemType.occurrences.length,
        });
      }
      onSelectItemType(itemType.diagram_item_type_id);
    },
    [selectedItemType, onSelectItemType],
  );

  const sortedItemTypes = useMemo(
    () =>
      sortTabList({
        items: selectedDiagram?.itemTypes ?? [],
        mode: sort,
        getLabel: (itemType) => itemType.item,
        getQuantity: (itemType) => itemType.totalCount,
      }),
    [selectedDiagram?.itemTypes, sort],
  );

  const normalizedBoardSearch = boardSearch.trim().toLowerCase();

  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <SearchInput
          value={boardSearch}
          onChange={onBoardSearchChange}
          placeholder={t("smartSurveyor.reviewPage.searchPlaceholder")}
          className="flex-1"
        />
        <TabSortControl value={sort} onChange={onSortChange} />
      </div>
      <GroupsApprovalBarV3
        summaryText={t("smartSurveyor.reviewPage.groupsCount", {
          count: selectedDiagramItemTypeIds.length,
        })}
        allApproved={allDiagramItemsApproved}
        onToggleApproval={onOpenBulkApproval}
        isLoading={isLoadingApprovals}
        canEdit={canEdit}
      />
      <ul className="overflow-hidden flex flex-col min-h-0">
        <ElementGroupScrollableDivideList containerRef={containerRef}>
          {sortedItemTypes
            .filter(
              (itemType) =>
                itemType.item.toLowerCase().includes(normalizedBoardSearch) ||
                itemType.diagram_item_type_id ===
                  selectedItemType?.diagram_item_type_id,
            )
            .map((itemType) => {
              const isSelected =
                itemType.diagram_item_type_id ===
                selectedItemType?.diagram_item_type_id;
              return (
                <ElementGroupDefaultRow
                  key={itemType.diagram_item_type_id}
                  title={itemType.item}
                  highlightQuery={normalizedBoardSearch}
                  subtitle={itemType.text}
                  count={itemType.totalCount}
                  isSelected={isSelected}
                  onClick={() => handleItemTypeClick(itemType)}
                  rowRef={isSelected ? targetRef : undefined}
                  className={
                    isQuantityGroupApproved(itemType.diagram_item_type_id)
                      ? "bg-green-50 hover:bg-green-100"
                      : undefined
                  }
                  containerClassName={
                    isSelected
                      ? "!border-2 !border-blue-400 rounded-lg overflow-hidden my-1"
                      : undefined
                  }
                  endAdornment={
                    <ApprovalStatusCheckbox
                      checked={isQuantityGroupApproved(
                        itemType.diagram_item_type_id,
                      )}
                      onToggle={() =>
                        onToggleQuantityGroupApproval(
                          itemType.diagram_item_type_id,
                        )
                      }
                    />
                  }
                >
                  {isSelected && selectedItemType && (
                    <BoardPageInspectorV3
                      canEdit={canEdit}
                      itemType={selectedItemType}
                      selectedOccurrenceIndex={selectedOccurrenceIndex}
                      onSelectOccurrence={onSelectOccurrence}
                      isTraversing={isTraversing}
                      onStartTraversing={onStartTraversing}
                      onStopTraversing={onStopTraversing}
                      seenIds={seenIds}
                      onGoNextUnseen={onGoNextUnseen}
                      onPrevOccurrence={onPrevOccurrence}
                      onNextOccurrence={onNextOccurrence}
                      boqPositions={boqPositions}
                      onSaveLvMatch={onSaveLvMatch}
                      onDeleteItemGroup={onDeleteItemGroup}
                    />
                  )}
                </ElementGroupDefaultRow>
              );
            })}
        </ElementGroupScrollableDivideList>
      </ul>
    </>
  );
};
